// ===== Menus de filtro da barra de Clientes no desktop (spec 2026-10-29-clientes-desktop-uma-linha-e-filtro-de-canal.md) =====
// `MenuDeFiltro` e o gatilho + o balao (mesma aparencia e mesmo posicionamento do `Select` de modals.jsx, que nao mudou: e usado em muitas telas).
// `MenuDeCanal` (lista com busca) e `MenuMaisFiltros` (Nicho, UF, "Com alertas" e "Reativar") preenchem o balao.
import React from 'react';
import { Icon } from './icons.jsx';
import { NICHOS, brlCompact } from './data.jsx';

const { useState, useRef, useMemo, useEffect, useLayoutEffect, useId } = React;

// Cada linha custa DOM e pintura; 400 canais desenhados de uma vez seriam o mesmo erro de desenhar os 2201 clientes de uma vez (clientes.jsx, POR_VEZ).
const LIMITE_DE_LINHAS = 100;
const ALTURA_DA_LISTA = 320;

// A busca ignora caixa e acento: "abaco" acha "Ábaco Luz".
const normalizar = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Mesma conta do `place()` do Select: abre para baixo se couber, senao para cima, senao no lado maior. A largura propria do balao pode passar
// da do gatilho; `left` recua para o balao nao sair da janela.
function calcularPosicao(gatilho, largura, alturaIdeal) {
  const r = gatilho.getBoundingClientRect();
  const abaixo = window.innerHeight - r.bottom - 10;
  const acima = r.top - 10;
  let lado; let maxH;
  if (abaixo >= alturaIdeal) { lado = 'below'; maxH = alturaIdeal; }
  else if (acima >= alturaIdeal) { lado = 'above'; maxH = alturaIdeal; }
  else if (abaixo >= acima) { lado = 'below'; maxH = abaixo; }
  else { lado = 'above'; maxH = acima; }
  const width = Math.max(r.width, largura);
  return {
    left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), width,
    top: lado === 'below' ? r.bottom + 4 : r.top - 4, lado, maxH: Math.max(120, maxH),
  };
}

// `children` pode ser funcao: recebe `{ fechar }`. `fechar(true)` devolve o foco ao gatilho (Esc, "Pronto"); sem o argumento o foco fica onde esta
// (clique fora, Tab). `onTecla` recebe as teclas do gatilho com o balao aberto (o foco fica no gatilho, como no `Select`).
function MenuDeFiltro({ rotulo, icone, contagem = 0, ativo = false, aberto, onAbrir, onFechar, largura = 300, alturaIdeal = 420, tituloDoGatilho, haspopup = 'dialog', onTecla, children }) {
  const gatilho = useRef(null);
  const balao = useRef(null);
  const [pos, setPos] = useState(null);
  const fechar = (comFoco) => { onFechar(); if (comFoco === true) gatilho.current?.focus(); };
  // O efeito so reexecuta quando `aberto` muda; o `onFechar` do pai muda de identidade a cada render.
  const fecharRef = useRef(fechar);
  fecharRef.current = fechar;

  useLayoutEffect(() => {
    if (!aberto) { setPos(null); return undefined; }
    const posicionar = () => { if (gatilho.current) setPos(calcularPosicao(gatilho.current, largura, alturaIdeal)); };
    posicionar();
    // Clique fora: nem o gatilho nem o balao. O `Select` usa `.closest('.select-pop')`; aqui varios baloes compartilham a classe, entao a referencia propria.
    const fora = (e) => { if (!gatilho.current?.contains(e.target) && !balao.current?.contains(e.target)) fecharRef.current(); };
    // Esc fecha na fase de captura e nao deixa o evento subir: a gaveta do cliente tambem fecha com Esc e sairia junto.
    const tecla = (e) => { if (e.key === 'Escape') { e.stopPropagation(); fecharRef.current(true); } };
    // Rolar a lista de dentro do balao nao pode reposicionar o balao.
    const rolagem = (e) => { if (!balao.current?.contains(e.target)) posicionar(); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', tecla, true);
    window.addEventListener('scroll', rolagem, true);
    window.addEventListener('resize', posicionar);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', tecla, true);
      window.removeEventListener('scroll', rolagem, true);
      window.removeEventListener('resize', posicionar);
    };
  }, [aberto, largura, alturaIdeal]);

  const aoTeclar = (e) => {
    if (!aberto) {
      if (e.key === 'ArrowDown') { e.preventDefault(); onAbrir(); }
      return;
    }
    if (e.key === 'Tab') { fechar(); return; }
    onTecla?.(e);
  };
  // O espaco aciona o botao ao soltar a tecla; com o balao aberto ele marca a linha (em `onTecla`) e nao pode tambem fechar o menu.
  const aoSoltar = (e) => { if (aberto && e.key === ' ' && onTecla) e.preventDefault(); };

  return (
    <>
      <button type="button" ref={gatilho} className={`chip-btn mdf-gatilho${ativo ? ' active' : ''}${aberto ? ' open' : ''}`} title={tituloDoGatilho}
        aria-haspopup={haspopup} aria-expanded={aberto} onClick={() => (aberto ? fechar() : onAbrir())} onKeyDown={aoTeclar} onKeyUp={aoSoltar}>
        <Icon name={icone} size={13} />
        <span>{contagem > 0 ? `${rotulo} · ${contagem}` : rotulo}</span>
        <Icon name="chevron-down" size={13} className="select-caret" />
      </button>
      {aberto && pos && (
        <div ref={balao} className={`select-pop multi mdf-pop ${pos.lado}`}
          style={{ left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxH, transform: pos.lado === 'above' ? 'translateY(-100%)' : 'none' }}
          onKeyDown={(e) => { if (e.key === 'Tab') fechar(); }}>
          {typeof children === 'function' ? children({ fechar }) : children}
        </div>
      )}
    </>
  );
}

// Rodape igual ao do `Select` multi: "Limpar" (desligado sem escolha) e "Pronto".
function Rodape({ podeLimpar, onLimpar, onPronto }) {
  return (
    <div className="select-multi-foot">
      <button type="button" className="smf-clear" onMouseDown={(e) => { e.preventDefault(); onLimpar(); }} disabled={!podeLimpar}>Limpar</button>
      <button type="button" className="smf-done" onMouseDown={(e) => { e.preventDefault(); onPronto(); }}>Pronto</button>
    </div>
  );
}

function CaixaDeMarcar({ marcada }) {
  return <span className={`opt-check ${marcada ? 'on' : ''}`}>{marcada && <Icon name="check" size={11} />}</span>;
}

// ---------- Canal: lista com busca ----------
function ListaDeCanais({ indice, pipelineByCanal, valor, onChange, fechar }) {
  const [busca, setBusca] = useState('');
  const [hi, setHi] = useState(0);
  const campo = useRef(null);
  const idLista = useId();
  useEffect(() => { campo.current?.focus({ preventScroll: true }); }, []);

  const marcados = useMemo(() => new Set(valor), [valor]);
  // Selecionados no topo; depois quem tem mais clientes; empate pelo nome. A fatia de 100 sai ANTES de desenhar.
  const { linhas, total } = useMemo(() => {
    const q = normalizar(busca.trim());
    const achados = indice.filter((i) => !q || i.chave.includes(q));
    achados.sort((a, b) => (marcados.has(b.e.id) - marcados.has(a.e.id)) || ((b.e.clientesCount || 0) - (a.e.clientesCount || 0)) || a.e.nome.localeCompare(b.e.nome, 'pt-BR'));
    return { linhas: achados.slice(0, LIMITE_DE_LINHAS), total: achados.length };
  }, [indice, busca, marcados]);

  const alternar = (id) => onChange(marcados.has(id) ? valor.filter((x) => x !== id) : [...valor, id]);
  const aoTeclar = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => (linhas.length ? (h + 1) % linhas.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (linhas.length ? (h - 1 + linhas.length) % linhas.length : 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (linhas[hi]) alternar(linhas[hi].e.id); }
  };
  useEffect(() => { document.getElementById(`${idLista}-${hi}`)?.scrollIntoView?.({ block: 'nearest' }); }, [hi, idLista]);
  const ativa = linhas[hi] ? `${idLista}-${hi}` : undefined;

  return (
    <>
      <div className="mdf-busca">
        <Icon name="search" size={13} style={{ color: 'var(--color-ink-soft)' }} />
        <input ref={campo} type="text" role="combobox" aria-expanded="true" aria-controls={idLista} aria-activedescendant={ativa} aria-label="Buscar canal"
          placeholder="Buscar canal..." value={busca} onChange={(e) => { setBusca(e.target.value); setHi(0); }} onKeyDown={aoTeclar} />
      </div>
      <div className="mdf-lista" id={idLista} role="listbox" aria-multiselectable="true" aria-label="Canais">
        {linhas.map((l, i) => {
          const sel = marcados.has(l.e.id);
          const andamento = pipelineByCanal[l.e.id] || 0;
          return (
            <button type="button" key={l.e.id} id={`${idLista}-${i}`} role="option" aria-selected={sel} tabIndex={-1}
              className={`select-opt mdf-canal ${i === hi ? 'active' : ''} ${sel ? 'selected' : ''}`}
              onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); alternar(l.e.id); }}>
              <CaixaDeMarcar marcada={sel} />
              <span className="so-label" title={l.e.nome}>{l.e.nome}</span>
              <span className="mdf-num">{l.e.clientesCount}</span>
              {andamento > 0 && <span className="mdf-and">{brlCompact(andamento)} em and.</span>}
            </button>
          );
        })}
        {linhas.length === 0 && <div className="mdf-info">Nenhum canal encontrado.</div>}
        {total > linhas.length && <div className="mdf-info">Digite para ver mais</div>}
      </div>
      <Rodape podeLimpar={valor.length > 0} onLimpar={() => onChange([])} onPronto={() => fechar(true)} />
    </>
  );
}

function MenuDeCanal({ canais, pipelineByCanal, valor, onChange, aberto, onAbrir, onFechar }) {
  // O texto sem acento de cada canal e calculado uma vez por lista, nao a cada tecla da busca.
  const indice = useMemo(() => canais.map((e) => ({ e, chave: normalizar(e.nome) })), [canais]);
  return (
    <MenuDeFiltro rotulo="Canal" icone="compass" contagem={valor.length} ativo={valor.length > 0} aberto={aberto} onAbrir={onAbrir} onFechar={onFechar}
      haspopup="listbox" largura={340} alturaIdeal={ALTURA_DA_LISTA + 100}>
      {({ fechar }) => <ListaDeCanais indice={indice} pipelineByCanal={pipelineByCanal} valor={valor} onChange={onChange} fechar={fechar} />}
    </MenuDeFiltro>
  );
}

// ---------- Mais filtros: Nicho, UF, "Com alertas" e "Reativar" ----------
function CorpoMaisFiltros({ nichos, ufs, filtros, onNicho, onUf, onAlertas, onReativar, onLimpar, fechar, teclaRef }) {
  const [hi, setHi] = useState(0);
  const idBase = useId();
  const linhas = useMemo(() => [
    ...nichos.map((n) => ({ tipo: 'nicho', valor: n.id, rotulo: n.label })),
    ...ufs.map((u) => ({ tipo: 'uf', valor: u, rotulo: u })),
    { tipo: 'alertas', rotulo: 'Com alertas', title: 'Somente clientes com alertas pendentes' },
    { tipo: 'reativar', rotulo: 'Reativar', title: 'Somente clientes indicados para reativação' },
  ], [nichos, ufs]);
  const marcada = (l) => (l.tipo === 'nicho' ? filtros.nicho.includes(l.valor) : l.tipo === 'uf' ? filtros.uf.includes(l.valor) : !!filtros[l.tipo]);
  const alternar = (l) => {
    if (l.tipo === 'nicho') onNicho(l.valor);
    else if (l.tipo === 'uf') onUf(l.valor);
    else if (l.tipo === 'alertas') onAlertas();
    else onReativar();
  };
  teclaRef.current = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); setHi((h) => (h + 1) % linhas.length); }
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); setHi((h) => (h - 1 + linhas.length) % linhas.length); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternar(linhas[hi]); }
  };
  useEffect(() => { document.getElementById(`${idBase}-${hi}`)?.scrollIntoView?.({ block: 'nearest' }); }, [hi, idBase]);
  const linha = (i, compacta) => {
    const l = linhas[i];
    const on = marcada(l);
    return (
      <button type="button" key={`${l.tipo}-${l.valor ?? ''}`} id={`${idBase}-${i}`} role="checkbox" aria-checked={on} tabIndex={-1} title={l.title}
        className={`select-opt ${compacta ? 'mdf-compacta' : ''} ${i === hi ? 'active' : ''} ${on ? 'selected' : ''}`}
        onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); alternar(l); }}>
        <CaixaDeMarcar marcada={on} />
        <span className="so-label">{l.rotulo}</span>
      </button>
    );
  };
  const nAtivos = blocosAtivos(filtros);
  return (
    <>
      <div className="mdf-corpo">
        <div className="mdf-bloco-titulo" id={`${idBase}-nicho`}>Nicho</div>
        <div role="group" aria-labelledby={`${idBase}-nicho`}>{nichos.map((n, k) => linha(k, false))}</div>
        <div className="mdf-bloco-titulo" id={`${idBase}-uf`}>UF</div>
        <div role="group" aria-labelledby={`${idBase}-uf`} className="mdf-grade">{ufs.map((u, k) => linha(nichos.length + k, true))}</div>
        <div className="mdf-bloco-titulo" id={`${idBase}-outros`}>Outros</div>
        <div role="group" aria-labelledby={`${idBase}-outros`}>{linha(linhas.length - 2, false)}{linha(linhas.length - 1, false)}</div>
      </div>
      <Rodape podeLimpar={nAtivos > 0} onLimpar={onLimpar} onPronto={() => fechar(true)} />
    </>
  );
}

// Conta BLOCOS ativos (0 a 4), nao itens marcados: tres UFs e "Reativar" mostram "Mais filtros · 2".
const blocosAtivos = (f) => [f.nicho.length > 0, f.uf.length > 0, !!f.alertas, !!f.reativar].filter(Boolean).length;

function MenuMaisFiltros({ ufs, filtros, onNicho, onUf, onAlertas, onReativar, onLimpar, aberto, onAbrir, onFechar }) {
  const tecla = useRef(null);
  const n = blocosAtivos(filtros);
  return (
    <MenuDeFiltro rotulo="Mais filtros" icone="filter" contagem={n} ativo={n > 0} aberto={aberto} onAbrir={onAbrir} onFechar={onFechar}
      largura={300} alturaIdeal={460} onTecla={(e) => tecla.current?.(e)}>
      {({ fechar }) => (
        <CorpoMaisFiltros nichos={NICHOS} ufs={ufs} filtros={filtros} onNicho={onNicho} onUf={onUf} onAlertas={onAlertas} onReativar={onReativar}
          onLimpar={onLimpar} fechar={fechar} teclaRef={tecla} />
      )}
    </MenuDeFiltro>
  );
}

export { MenuDeFiltro, MenuDeCanal, MenuMaisFiltros, LIMITE_DE_LINHAS, normalizar };
